import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import { PRIORITY_COLORS } from "../lib/constants";
import { Calculator, ArrowCounterClockwise, MagicWand, Scroll, Function as FxIcon, FileArrowDown, ShieldCheck } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { EquipmentSwitcher } from "../components/EquipmentSwitcher";
import ResultsTabs from "../components/design/ResultsTabs";
import OptimizeModal from "../components/design/OptimizeModal";
import CompareVersions from "../components/design/CompareVersions";
import CustomCalcsPanel from "../components/design/CustomCalcsPanel";

const FALLBACK_SLIDERS = [
  { key: "flux_density_t", label: "Flux Density", unit: "T", min: 1.4, max: 1.85, step: 0.01 },
  { key: "j_hv", label: "J HV", unit: "A/mm²", min: 1.5, max: 4.5, step: 0.05 },
  { key: "j_lv", label: "J LV", unit: "A/mm²", min: 1.5, max: 4.5, step: 0.05 },
  { key: "core_steps", label: "Core Steps", unit: "", min: 4, max: 10, step: 1 },
  { key: "window_ratio", label: "Window Ratio", unit: "", min: 0.8, max: 1.3, step: 0.01 },
];

const FALLBACK_SPEC_INPUTS = [
  { key: "rating_kva", label: "Rating", unit: "kVA" },
  { key: "hv_rated_kv", label: "HV Voltage", unit: "kV" },
  { key: "lv_rated_kv", label: "LV Voltage", unit: "kV" },
  { key: "frequency_hz", label: "Frequency", unit: "Hz" },
  { key: "impedance_pct", label: "Impedance", unit: "%" },
  { key: "no_load_loss_w", label: "No-Load Loss guarantee", unit: "W" },
  { key: "load_loss_75c_w", label: "Load Loss guarantee", unit: "W" },
  { key: "ambient_temp_max_c", label: "Max Ambient", unit: "°C" },
];

export default function DesignEngine({ embedded = false }) {
  const { projects, pid, setPid, reload: reloadProjects } = useProject();
  const [searchParams] = useSearchParams();
  // the validation fix guide links straight to the knob it wants changed: ?tab=design&focus=j_hv
  const focus = searchParams.get("focus") || "";
  const [design, setDesign] = useState(null);
  const [inputs, setInputs] = useState({});
  // key variables and specification inputs are pack-driven — they come from the
  // project's registered calculation inputs, not a fixed transformer list
  const sliders = (design?.inputs_meta?.length ? design.inputs_meta : FALLBACK_SLIDERS);
  const specInputs = (design?.spec_meta?.length ? design.spec_meta : FALLBACK_SPEC_INPUTS);
  const [specs, setSpecs] = useState({});
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showOptimize, setShowOptimize] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showCalcs, setShowCalcs] = useState(false);
  const [autoDefaults, setAutoDefaults] = useState({});
  const [recBusy, setRecBusy] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!pid) return;
    setResults(null);
    api.get(`/design/${pid}`).then(({ data }) => {
      setDesign(data);
      setInputs(data.inputs);
      setSpecs(data.specs);
      setAutoDefaults(data.inputs);
      if (data.last_results) setResults(data.last_results);
    }).catch((e) => toast.error(fmtErr(e)));
  }, [pid]);

  const calculate = async (overrides, specsOverride) => {
    if (!pid) return;
    setCalculating(true);
    const started = Date.now();
    try {
      const { data } = await api.post(`/design/${pid}/calculate`, { inputs: overrides || inputs, specs: specsOverride || specs });
      setResults({ modules: data.modules, metrics: data.metrics, trace: data.trace });
      setInputs(data.inputs);
      reloadProjects();   // keep Drawing Studio / Documents in sync with the new results
    } catch (e) { toast.error(fmtErr(e), { duration: 7000 }); }
    finally { setTimeout(() => setCalculating(false), Math.max(0, 450 - (Date.now() - started))); }
  };

  const onInput = (key, value) => {
    const next = { ...inputs, [key]: Number(value) };
    setInputs(next);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => calculate(next), 700);
  };

  const onSpec = (key, value) => setSpecs((s) => ({ ...s, [key]: value === "" ? "" : Number(value) }));

  const setFromIntake = async () => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/design/${pid}`);
      const base = data.pack_inputs || {};
      const nextIn = Object.keys(base).length ? base : data.inputs;
      setDesign(data);
      setInputs(nextIn);
      setSpecs(data.specs);
      toast.success("Key variables reset to the pack baseline and specification inputs reloaded from Intake");
      calculate(nextIn, data.specs);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setFromValidation = async () => {
    if (!pid) return;
    setRecBusy(true);
    try {
      const { data } = await api.get(`/validation/${pid}/recommendations`);
      if (!data.ok) { toast.warning(data.reason || "Nothing to apply", { duration: 8000 }); return; }
      const nextIn = { ...inputs, ...data.inputs };
      const nextSp = { ...specs, ...data.specs };
      setInputs(nextIn);
      setSpecs(nextSp);
      // spec values are guarantees — they live in Intake, so they are written to the project
      // (and the team notified) through the same validated path the fix guide uses
      const failed = [];
      for (const c of data.spec_changes || []) {
        try {
          await api.post(`/validation/${pid}/issues/${encodeURIComponent(c.issue_id)}/apply-fix`,
            { specs_patch: { [c.field]: c.suggested } });
        } catch { failed.push(c.label || c.field); }
      }
      const knobs = (data.input_changes || [])
        .map((c) => `${c.label} → ${c.suggested}${c.unit ? ` ${c.unit}` : ""} (${c.check_id})`).join(", ");
      const guarantees = (data.spec_changes || [])
        .filter((c) => !failed.includes(c.label || c.field))
        .map((c) => `${c.label} → ${c.suggested}${c.unit ? ` ${c.unit}` : ""} (${c.check_id})`).join(", ");
      toast.success(
        [knobs && `Design variables set: ${knobs}`,
         guarantees && `Intake updated and team notified: ${guarantees}`,
         `Blockers ${data.blockers_now} → ${data.blockers_after}`].filter(Boolean).join(" · "),
        { duration: 10000 });
      if (failed.length) toast.error(`Could not write to Intake: ${failed.join(", ")}`, { duration: 9000 });
      calculate(nextIn, nextSp);
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); }
    finally { setRecBusy(false); }
  };

  const project = projects.find((p) => p.id === pid);
  const noEngine = design && design.modules.length === 0;
  const customsOn = specs?.include_custom_calcs !== false;

  const toggleCustoms = () => {
    const next = { ...(specs || {}), include_custom_calcs: !customsOn };
    setSpecs(next);
    toast.success(`Custom formulas ${customsOn ? "excluded from" : "included in"} design runs`);
    calculate(undefined, next);
  };
  const activeVerId = design?.versions?.find((v) => v.version === design.active_version)?.id || "";

  useEffect(() => {
    if (!focus || !design) return;
    const el = document.querySelector(`[data-testid="slider-row-${focus}"], [data-testid="spec-in-${focus}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focus, design]);

  const focusCls = (key) => (key === focus
    ? "ring-2 ring-[var(--c-accent)] rounded-sm bg-[var(--c-accent)]/10 px-2 -mx-2 py-1" : "");

  const switchVersion = async (vid) => {
    try {
      await api.post(`/design/${pid}/versions/${vid}/apply`);
      const { data } = await api.get(`/design/${pid}`);
      setDesign(data);
      setInputs(data.inputs);
      if (data.last_results) setResults(data.last_results);
      toast.success("Version activated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className={embedded ? "" : "p-6"} data-testid="design-engine-page">
      <div className="flex items-center gap-4 flex-wrap">
        {!embedded && (
          <>
            <div>
              <h2 className="text-base font-medium text-[var(--c-text)]">Design Engine</h2>
              <p className="text-xs text-[var(--c-muted)] mt-0.5">Calculation modules load from the project's industry pack registry</p>
            </div>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="design-project-select" className="w-96 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    <span className="font-mono text-[var(--c-muted)] mr-2">{p.project_code}</span>{p.product_summary}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <EquipmentSwitcher projectId={pid} onSwitched={() => window.location.reload()} />
          </>
        )}
        {calculating && <span className="font-mono text-[10px] text-[var(--c-accent)] tracking-widest" data-testid="calculating-indicator">CALCULATING…</span>}
        {design && (design.versions || []).length > 0 && (
          <>
            <Select value={activeVerId} onValueChange={switchVersion}>
              <SelectTrigger data-testid="version-select" className="w-72 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue placeholder="Version…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {[...design.versions].sort((a, b) => (b.version || 0) - (a.version || 0)).map((v) => (
                  <SelectItem key={v.id} value={v.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">
                    <span className="font-mono text-[var(--c-muted)] mr-2">v{v.version ?? "—"}</span>{v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!activeVerId && (
              <span className="text-[9px] font-mono tracking-wider text-[var(--c-amber)] uppercase" data-testid="unsaved-working-copy">
                Unsaved working copy
              </span>
            )}
            <Button variant="outline" size="sm" data-testid="compare-versions-btn" onClick={() => setShowCompare(true)}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
              Compare
            </Button>
          </>
        )}
      </div>

      {showCompare && design && (
        <CompareVersions versions={design.versions} onClose={() => setShowCompare(false)} />
      )}

      {noEngine && (
        <div className="mt-6 bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-5 text-sm text-[var(--c-text)]" data-testid="no-engine-note">
          No calculation modules registered for <b>{project?.industry?.replace(/_/g, " ")}</b> yet. Power Transformers is the reference implementation — other industries plug in via the same registry.
        </div>
      )}

      {design && !noEngine && (
        <div className="flex gap-4 mt-5 items-start">
          <div className="w-80 shrink-0 space-y-4">
            <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="design-inputs">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Key Variables</div>
              <div className="mb-4 grid grid-cols-2 gap-1.5" data-testid="design-source-buttons">
                <button type="button" data-testid="set-from-intake-btn" onClick={setFromIntake}
                  title="Reload every key variable and specification input from the project's Intake"
                  className="flex items-center justify-center gap-1 rounded-sm border border-[var(--c-border)] px-2 py-1.5 text-[10px] text-[var(--c-text)] transition-colors duration-150 hover:border-[var(--c-accent)] hover:text-[var(--c-accent)]">
                  <FileArrowDown size={12} /> Set from Intake
                </button>
                <button type="button" data-testid="set-from-validation-btn" onClick={setFromValidation}
                  disabled={recBusy}
                  title="Set the variables to the values the validation engine says clear the open checks"
                  className="flex items-center justify-center gap-1 rounded-sm border border-[var(--c-green)]/50 px-2 py-1.5 text-[10px] text-[var(--c-green)] transition-colors duration-150 hover:bg-[var(--c-green)]/10 disabled:opacity-50">
                  <ShieldCheck size={12} /> {recBusy ? "Checking…" : "Set from Validation"}
                </button>
              </div>
              {sliders.map((s) => (
                <div key={s.key} className={`mb-4 ${focusCls(s.key)}`} data-testid={`slider-row-${s.key}`}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-[var(--c-muted)]">{s.label}</span>
                    <span className="font-mono text-[var(--c-accent)]">{inputs[s.key]}{s.unit && ` ${s.unit}`}</span>
                  </div>
                  {s.key === focus && (
                    <div className="mb-1 text-[10px] text-[var(--c-accent)]" data-testid={`focus-hint-${s.key}`}>
                      Validation sent you here — move this, then re-run Validation to see it clear
                    </div>
                  )}
                  <input type="range" data-testid={`slider-${s.key}`} min={s.min} max={s.max} step={s.step}
                    value={inputs[s.key] ?? s.min} onChange={(e) => onInput(s.key, e.target.value)}
                    className="w-full accent-[var(--c-accent)]" />
                </div>
              ))}
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2 mt-5 border-t border-[var(--c-border)] pt-3">Specification Inputs</div>
              <div className="grid grid-cols-2 gap-2">
                {specInputs.map((s) => (
                  <div key={s.key} className={`space-y-1 ${focusCls(s.key)}`}>
                    <Label className="text-[10px] text-[var(--c-muted)]">{s.label}{s.unit && <span className="font-mono"> ({s.unit})</span>}</Label>
                    <Input data-testid={`spec-in-${s.key}`} type="number" step="any" value={specs[s.key] ?? ""}
                      onChange={(e) => onSpec(s.key, e.target.value)}
                      className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs font-mono" />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[var(--c-muted)] mt-2">Spec edits apply on Calculate (not persisted to project specs).</p>
            </div>

            <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="design-constraints">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">
                Client Rules · constraints ({design.rules_snapshot.length})
              </div>
              <div className="space-y-1.5">
                {design.rules_snapshot.map((r) => (
                  <div key={r.rule_id} className="flex items-center gap-2 text-[11px]" data-testid={`constraint-${r.rule_id.slice(0, 8)}`}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_COLORS.medium, opacity: r.priority === "override" ? 1 : 0.6 }} />
                    <span className="text-[var(--c-text)] truncate">{r.name}</span>
                    <span className="font-mono text-[9px] text-[var(--c-muted)] ml-auto shrink-0">{r.parameter}</span>
                  </div>
                ))}
                {design.rules_snapshot.length === 0 && <div className="text-[11px] text-[var(--c-muted)]">No client rules on this project.</div>}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button data-testid="calculate-btn" onClick={() => calculate()} disabled={calculating}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
                <Calculator size={14} className="mr-1.5" /> {calculating ? "Calculating…" : "Calculate"}
              </Button>
              <Button data-testid="reset-auto-btn" variant="outline"
                onClick={() => { setInputs(autoDefaults); calculate(autoDefaults); }}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
                <ArrowCounterClockwise size={14} className="mr-1.5" /> Reset to Auto
              </Button>
              <Button data-testid="optimize-btn" onClick={() => setShowOptimize(true)}
                className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
                <MagicWand size={14} className="mr-1.5" /> Auto-Optimize
              </Button>
              <button data-testid="toggle-custom-calcs-btn" onClick={toggleCustoms}
                title="Include your org's custom formulas in design runs (set up via Custom Formulas)"
                className={`h-9 px-3 rounded-sm border text-xs font-medium text-left flex items-center gap-1.5 ${customsOn ? "border-[var(--c-green)]/50 text-[var(--c-green)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                <FxIcon size={14} /> {customsOn ? "Custom formulas: ON" : "Custom formulas: OFF"}
              </button>
              <Button data-testid="custom-formulas-btn" variant="outline" onClick={() => setShowCalcs(true)}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
                <FxIcon size={14} className="mr-1.5" /> Custom Formulas
              </Button>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            {results ? (
              <ResultsTabs results={results} specs={specs} projectId={pid} inputs={inputs}
                versions={design.versions} moduleDocs={design.module_docs} onVersionsChange={() => {
                  api.get(`/design/${pid}`).then(({ data }) => setDesign(data));
                }} />
            ) : (
              <div className="bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-14 text-center" data-testid="results-empty">
                <Calculator size={28} className="mx-auto text-[var(--c-muted)]" />
                <div className="text-sm text-[var(--c-text)] mt-3">No results yet</div>
                <div className="text-xs text-[var(--c-muted)] mt-1">Press Calculate — or drag a slider for live recalculation.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCalcs && project && (
        <CustomCalcsPanel open={showCalcs} onOpenChange={setShowCalcs} projectId={pid} industry={project.industry}
          onChanged={() => results && calculate()} />
      )}

      {showOptimize && (
        <OptimizeModal projectId={pid} inputs={inputs} specs={specs} rules={design?.rules_snapshot || []}
          searchBounds={design?.optimize_bounds}
          onClose={() => setShowOptimize(false)}
          onApplied={(bestInputs) => {
            setShowOptimize(false);
            setInputs((i) => ({ ...i, ...bestInputs }));
            calculate({ ...inputs, ...bestInputs });
          }} />
      )}
    </div>
  );
}
